import clsx from 'clsx'
import ReactDOM from "react-dom"
import React, { FC } from "react"
import { toast } from "react-toastify"
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"

import { Loading } from "../../components/utils/Loading"
import { Thematic } from '../../components/utils/Thematic'
import CookieServices from '../../services/CookieServices'
import { COOKIE_KEYS, APPLICATION } from '../../utils/Constants'

interface Props {
    size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl',
    status: any,
    header: any,
    show: boolean,
    contents: any,
    showOrHide: any,
    formAction: any,
    posting: boolean,
    noAction?: boolean,
    onSubmit: (e: any) => void,
    phase?: {
        prevHandler: () => void,
        prev: any,
        next: any,
    },
    error?: {
        title: string,
        message: any,
    },
}

export const TwoToneModal: FC<Props> = ({ show, size, showOrHide, onSubmit, posting, contents, formAction, status, error, noAction = false, header, phase = undefined }) => {
    const theme = CookieServices.get(COOKIE_KEYS.THEME) || APPLICATION.THEME

    const checkIfFormIsPostingData = () => {
        if (!posting) {
            showOrHide()
            return
        }

        let toastText = 'Cannot dismiss. Current operation is still in progress'

        toast.warning(toastText, {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    const widthClasses = {
        'sm': 'md:max-w-sm',
        'md': 'md:max-w-md',
        'lg': 'md:max-w-lg',
        'xl': 'md:max-w-xl',
        '2xl': 'md:max-w-2xl',
        '3xl': 'md:max-w-3xl',
        '4xl': 'md:max-w-4xl',
        '5xl': 'md:max-w-5xl',
        '6xl': 'md:max-w-6xl',
    };

    return ReactDOM.createPortal(
        <Dialog open={show} onClose={checkIfFormIsPostingData} className="relative z-20">
            <DialogBackdrop transition className="fixed inset-0 bg-slate-500 dark:bg-slate-700/70 backdrop-blur-sm bg-opacity-65 transition-opacit data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in" />

            <div className="fixed inset-0 z-20 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center w-full text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className={clsx(
                            size && widthClasses[size],
                            "relative transform w-full overflow-hidden md:rounded-lg text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95")}
                    >
                        <Thematic>
                            {
                                status === 'rejected' ? (
                                    <div className="bg-white dark:bg-slate-800">
                                        {
                                            error ? (
                                                <>
                                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                                        <div className="sm:flex sm:items-start">
                                                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                                                <span className="fal fa-exclamation-circle fa-2x text-red-600"></span>
                                                            </div>
                                                            <div className="mt-3 text-center text-slate-600 sm:mt-0 sm:ml-4 sm:text-left">
                                                                <span className="text-red-500 pb-4 pt-2 block">
                                                                    {error.title}
                                                                </span>

                                                                <div className="text-sm leading-7 mb-4">
                                                                    {error.message}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="bg-slate-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                                        <button
                                                            type="button"
                                                            onClick={checkIfFormIsPostingData}
                                                            className="w-full inline-flex justify-center text-sm rounded-md border border-transparent shadow-sm px-3 py-1 bg-red-600 text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                                                            Close
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="bg-white flex text-red-600 md:flex-row align-middle items-center flex-col px-4 pt-3 pb-4">
                                                        <div className="flex-none flex align-middle justify-center items-center md:w-24 py-3 w-full">
                                                            <span className="fa-duotone fa-cloud-exclamation fa-3x"></span>
                                                        </div>

                                                        <div className="flex-grow flex flex-col items-center">
                                                            <div className="mt-3 m-auto text-slate-600">
                                                                <span className="text-red-600 text-lg mb-2 block">
                                                                    Communication Breakdown
                                                                </span>

                                                                <div className="text-sm">
                                                                    We had some trouble processing your request. Try again later
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-slate-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                                        <div className="w-12/12 space-y-px">
                                                            <div className="flex flex-row-reverse items-center align-middle">
                                                                <button type="button" className="w-full inline-flex justify-center text-sm rounded-md border-0 border-transparent shadow-none px-3 py-1 bg-inherit text-orange-500 hover:bg-slate-200 sm:ml-3 sm:w-auto sm:text-sm" onClick={checkIfFormIsPostingData}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        }
                                    </div>
                                ) : status === 'fulfilled' ? (
                                    <form className="rounded-md bg-white dark:bg-slate-800 h-screen md:h-auto shadow-none space-y-px flex flex-col dark:text-slate-300 md:bg-slate-100 dark:md:bg-slate-800" onSubmit={onSubmit}>
                                        <div className="flex-grow flex flex-col md:flex-row md:px-4 md:py-4 md:overflow-hidden overflow-y-auto">
                                            <div className="md:w-1/3 flex-none w-full px-2.5 md:px-0 md:pb-0 md:pt-2 pt-5 bg-slate-100 dark:bg-slate-800 flex flex-col">
                                                {
                                                    header
                                                }

                                                <div className="w-full p-2">
                                                    {
                                                        !noAction && (
                                                            <button type="button" className="text-sm flex-none shadow-none px-3 py-1 bg-inherit text-slate-600 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:underline hover:cursor-pointer mr-2 sm:w-auto sm:text-sm" onClick={checkIfFormIsPostingData}>
                                                                Cancel
                                                            </button>
                                                        )
                                                    }
                                                </div>
                                            </div>

                                            <div className="md:flex-1 bg-white dark:bg-slate-800 md:shadow-lg dark:shadow-none rounded-lg dark:rounded-none w-full pt-4 dark:pt-0 dark:md:pt-4 md:border-l border-slate-300 dark:border-slate-600 flex flex-col">
                                                <div className="w-full min-h-[640px] max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-6rem)] md:max-h-[640px] md:overflow-y-auto overflow-hidden flex flex-col">
                                                    {
                                                        contents
                                                    }
                                                </div>

                                                <div className="w-full flex flex-row-reverse items-center justify-between p-3">
                                                    {
                                                        phase ? (
                                                            <div className="flex flex-row-reverse gap-x-3 align-middle">
                                                                <button type="submit" className="w-auto min-w-28 disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-md px-3 py-1.5 bg-orange-600 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-500 focus:outline-none flex justify-center items-center gap-x-2">
                                                                    {
                                                                        posting ? (
                                                                            <span className="flex flex-row items-center h-5 justify-center">
                                                                                <i className="fa-duotone fa-spinner-third fa-xl fa-spin"></i>
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                <span>{formAction}</span>

                                                                                {
                                                                                    phase?.next ? (
                                                                                        <svg xmlns="http://www.w3.org" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4 transition-opacity stroke-white">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                                                                                        </svg>
                                                                                    ) : (
                                                                                        <>
                                                                                            <svg width="24" height="24" viewBox="0 0 24 24" className="transition-all duration-300 size-5">
                                                                                                <circle cx="12" cy="12" r="10" className="stroke-white dark:fill-none" strokeWidth="2" fill="none" />
                                                                                                <path d="M8 12l3 3l5-5" className="stroke-white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                                                                            </svg>
                                                                                        </>
                                                                                    )
                                                                                }
                                                                            </>
                                                                        )
                                                                    }
                                                                </button>

                                                                <button type="button" onClick={phase.prevHandler} disabled={posting} className={`w-auto min-w-28 ${phase.prev === null ? 'hidden' : ''} disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-none px-3 py-1.5 bg-inherit text-orange-600 hover:text-orange-700 dark:text-orange-300 dark:hover:text-orange-200 focus:outline-none flex justify-center items-center align-middle gap-x-2.5 dark:disabled:text-orange-500 dark:disabled:hover:bg-slate-700 disabled:text-orange-400`}>
                                                                    <i className="fa-regular fa-chevron-left"></i>
                                                                    <span>Previous</span>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {/* Cancel & Next on the far right */}
                                                                <div className="flex w-full">
                                                                    <div className="flex-1 flex flex-row-reverse">
                                                                        <button type="submit" className="w-auto min-w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-3 py-1.5 bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-500 focus:outline-none">
                                                                            {
                                                                                posting ? (
                                                                                    <span className="flex flex-row items-center h-5 justify-center">
                                                                                        <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                                                                                    </span>
                                                                                ) : (
                                                                                    formAction
                                                                                )
                                                                            }
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="h-screen md:h-auto flex flex-col align-middle justify-center items-center md:px-0 px-6">
                                        <div className="py-2 px-6 bg-white dark:bg-slate-800 w-full rounded-lg">
                                            <Loading />
                                        </div>
                                    </div>
                                )
                            }
                        </Thematic>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>,
        document.getElementById('root')
    )
}